import { AccountVM } from './../../../../models/account';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { UserFacade } from './../../../../store/user/facade/user.facade';
import { AppConfig } from './../../../../app.config';
import { Router } from '@angular/router';
import { IChangePasswordState } from './../../../../store/state/auth.changepassword.state';
import { getUserState } from './../../../../store/user/selectors/user.selectors';
import { IAppState } from './../../../../store/state/app.state';
import { IUserState } from './../../../../store/state/user.state';
import { TFASteps, ChangedPasswordVM } from '../../../../models/auth';
import { Store, select } from '@ngrx/store';
import { UntypedFormGroup, UntypedFormControl, NgForm, Validators } from '@angular/forms';
import { Component, OnInit, ViewChild, OnDestroy, Inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatchPassword } from '../../../../utils/password-validation';
import { twoFactorType } from '../../../../models/twoFactorTypes';
import * as AuthActions from '../../../../store/auth/actions/auth.actions';
import * as UserActions from '../../../../store/user/actions/user.actions';
import { getChangePasswordState } from 'src/app/store/auth/selectors/auth.selectors';
import { take } from "rxjs/operators";
import { SMSVerificationStatus } from 'src/app/models/smsVerificationStatus';

@UntilDestroy()
@Component({
  selector: 'app-security-preferences',
  templateUrl: './security-preferences.component.html',
  styleUrls: ['./security-preferences.component.scss']
})
export class SecurityPreferencesComponent implements OnInit, OnDestroy {

  @ViewChild('f') form: NgForm;

  userState: IUserState;
  accountSecurityForm: UntypedFormGroup;
  userEntityId: string;

  userSubscription: Subscription;
  twoFactorEnabled: boolean;
  twoFactorType: number;
  twoFactorTypeLabel: string;
  showSpinner = false;
  loading = false;
  failText: string;
  authSubscription: Subscription;
  userProfileSubscription: Subscription;
  access_token: string;
  passwordChangeFail: boolean;
  passwordChangeFailText: string;
  passwordUpdatedSuccess = false;
  successText: string;
  showUpdateSuccessfulText = false;

  constructor(
    private appConfig: AppConfig,
    private store: Store<IAppState>,
    public dialog: MatDialog,
    private router: Router,
    private userFacade: UserFacade) { }

  ngOnInit() {
    this.store.dispatch(new AuthActions.ChangePasswordReset());

    this.accountSecurityForm = new UntypedFormGroup(
      {
        'oldPassword': new UntypedFormControl(null, [Validators.required]),
        'password': new UntypedFormControl(null, [Validators.required, Validators.pattern('(^.*(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*_0-9]).*$)')]),
        'confirmPassword': new UntypedFormControl(null, [Validators.required, Validators.pattern('(^.*(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*_0-9]).*$)')])
      },
      MatchPassword
    );

    this.userSubscription = this.store.pipe(select(getUserState))
      .subscribe((state: IUserState) => {
        this.userState = state;
        this.loading = state.viewProfile.loading;
        this.failText = state.viewProfile.failText;
        this.showSpinner = this.loading;
        this.setTwoFactorTypeLabel();
      });

    this.userProfileSubscription = this.userFacade.userProfileWhenNotNull$.pipe(untilDestroyed(this)).subscribe((userProfile: AccountVM) => {
      this.userEntityId = userProfile.userEntityId;
      this.twoFactorEnabled = userProfile.twoFactorEnabled;
      this.twoFactorType = userProfile.twoFactorType;
    });

    this.authSubscription = this.store.pipe(select(getChangePasswordState)).subscribe((state: IChangePasswordState) => {
      this.showSpinner = state.loading;
      this.passwordChangeFail = state.changeFail;
      this.passwordChangeFailText = state.changeFailText;
      this.passwordUpdatedSuccess = state.changeSuccess;
      this.successText = state.changeSuccessText;

      if (this.passwordUpdatedSuccess) {
        this.form.resetForm();
      }

      if (this.form && this.form.form) {
        if (!this.showSpinner) {
          this.form.form.enable();
        } else if (this.showSpinner) {
          this.form.form.disable();
        }
      }
      this.showUpdateSuccessfulText = (this.passwordUpdatedSuccess && !this.loading);
    });

  }

  toggleShowTFA() {
    this.openDialog();
  }

  onInputChange(form: NgForm) {
    this.showUpdateSuccessfulText = false;
    if (form.valid) {
      this.passwordChangeFail = false;
    }
  }

  onBlur(form: NgForm) {
    if (!form.valid) {
      this.processError(form);
    } else {
      this.passwordChangeFail = false;
    }
  }

  processError(form: NgForm) {
    const formControls = form.form.controls;
    for (const controlKey in formControls) {
      if (formControls[controlKey]) {
        const control = formControls[controlKey];
        if (control.status !== 'VALID') {
          const errors = control.errors;
          for (const error in errors) {
            if (error) {
              switch (error) {
                case 'required':
                  break;
                case 'MatchPassword':
                  this.passwordChangeFail = true;
                  this.passwordChangeFailText = "Passwords don't match";
                  break;
                case 'pattern':
                  this.passwordChangeFail = true;
                  this.passwordChangeFailText = "Password doesn't meet requirements";
                  break;
                default:
                  return 'One or more fields are incorrect';
              }
            }
          }
        }
      }
    }
  }

  onSubmit(form: NgForm) {
    const model: ChangedPasswordVM = {
      oldPassword: form.value.oldPassword,
      newPassword: form.value.password,
      confirmedPassword: form.value.confirmPassword,
      siteId: Number.parseInt(this.appConfig.getEnvironment().siteId, 10)
    };

    this.showUpdateSuccessfulText = false;

    this.store.dispatch(new AuthActions.ChangePassword(model));
  }

  onTFAToggle(e: MatSlideToggleChange) {
    this.twoFactorEnabled = e.checked;
    this.store.dispatch(new UserActions.UpdateTFA(this.twoFactorEnabled));
  }

  openDialog(): void {
    this.store.dispatch(new UserActions.TFAStep(0));
    const dialogRef = this.dialog.open(TFASettingsDialogComponent, {
      width: '720px',
      disableClose: true
    });
  }

  closeSideBar() {
    if (!this.showSpinner) {
      setTimeout(() => {
        this.router.navigate([{ outlets: { 'sidebar': null } }]);
      }, 300);
      this.store.dispatch(new UserActions.UserProfileEditClose());
    }
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
    this.authSubscription.unsubscribe();
  }

  private setTwoFactorTypeLabel(): void {

    switch (this.twoFactorType) {
      case twoFactorType.none:
        this.twoFactorTypeLabel = 'none';
        break;
      case twoFactorType.email:
        this.twoFactorTypeLabel = 'EMAIL';
        break;
      case twoFactorType.sms:
        this.twoFactorTypeLabel = 'SMS';
        break;
      default:
        this.twoFactorTypeLabel = 'none';
    }
  }
}


@UntilDestroy()
@Component({
  selector: 'app-tfa-settings-dialog',
  templateUrl: 'tfa-settings-dialog.component.html',
  styleUrls: ['./tfa-settings-dialog.component.scss']
})
export class TFASettingsDialogComponent implements OnInit, OnDestroy {

  @ViewChild('securityForm') securityForm: NgForm;
  @ViewChild('verificationForm') verificationForm: NgForm;

  userState: IUserState;

  userSubscription: Subscription;
  userProfileSubscription: Subscription;
  usersMobileNumberSubscription: Subscription;
  userNeedsToSetTwoFactorAuthenticationSubscription: Subscription;

  accountSecurityTFAForm: UntypedFormGroup;
  mobileVerificationForm: UntypedFormGroup;

  twoFactorType: number;
  userProfile: AccountVM;
  userEntityId: string;
  twoFactorEnabled: boolean;
  twoFactorPhone: string;

  validMobilePhone = false;
  profileTFATypeUpdatedSuccess = false;
  requestSMSVerificationSuccess = false;
  SMSVerificationStatus = SMSVerificationStatus.undefined;
  profileUpdatedSuccess = false;
  loading = false;
  showSpinner = false;
  failText: string;
  TFAStep: TFASteps;
  hideCloseButtonOnTFAModal: boolean = false;
  twoFactorIsMobileVerified: boolean;

  // Config variables
  managerName: string;

  SMSVerifiactionFailed: SMSVerificationStatus = SMSVerificationStatus.failure;
  SMSVerifiactionSuccess: SMSVerificationStatus = SMSVerificationStatus.success;

  constructor(
    private store: Store<IAppState>,
    public dialogRef: MatDialogRef<TFASettingsDialogComponent>,
    private userFacade: UserFacade,
    private appConfig: AppConfig,
    @Inject(MAT_DIALOG_DATA) public data: {}
  ) { }

  ngOnInit() {
    this.managerName = this.appConfig.getEnvironment().name;

    this.store.dispatch(new UserActions.ResetProfileTFA());
  
    this.accountSecurityTFAForm = new UntypedFormGroup(
      {
        'twoFactorType': new UntypedFormControl(null),
        'profile_mobile_phone': new UntypedFormControl(null, [Validators.required, Validators.minLength(8), Validators.maxLength(18), Validators.pattern('^[0-9\+]*[0-9 ]*[0-9]$')]),
      }
    );

    this.mobileVerificationForm = new UntypedFormGroup(
      {
        'verificationCode': new UntypedFormControl(null, [Validators.required, Validators.minLength(4), Validators.maxLength(4)])
      }
    );

    this.userSubscription = this.store.pipe(select(getUserState)).subscribe((state: IUserState) => {

      if (!state.viewProfile) {
        return;
      }

      this.userState = state;
      this.profileTFATypeUpdatedSuccess = state.viewProfile.profileTFATypeUpdatedSuccess;
      this.requestSMSVerificationSuccess = state.viewProfile.requestSMSVerificationSuccess;
      this.profileUpdatedSuccess = state.viewProfile.profileUpdatedSuccess;
      this.failText = state.viewProfile.failText;
      this.SMSVerificationStatus = state.viewProfile.SMSVerificationStatus;
      this.showSpinner = this.loading = state.viewProfile.loading;
      this.TFAStep = state.viewProfile.TFAStep;

      if (this.securityForm && this.securityForm.form) {
        if (!this.loading) {
          this.securityForm.form.enable();
        } else if (this.loading) {
          this.securityForm.form.disable();
        }
      }

      if (this.verificationForm && this.verificationForm.form) {
        if (!this.loading) {
          this.verificationForm.form.enable();
        } else if (this.loading) {
          this.verificationForm.form.disable();
        }
      }

      if (this.TFAStep === TFASteps.SMS_REQUEST) {
        this.setMobilePhoneValue();
      }

      if (this.TFAStep === TFASteps.SMS_CONFIRM) {        
        this.mobileVerificationForm.get('verificationCode').disable();       
      }
    });

    this.userFacade.userProfileWhenNotNull$.pipe(untilDestroyed(this)).subscribe((userProfile: AccountVM) => {
      this.userProfile = userProfile;
      this.userEntityId = userProfile.userEntityId;
      this.twoFactorEnabled = userProfile.twoFactorEnabled;
      this.twoFactorType = userProfile.twoFactorType;
      this.twoFactorPhone = this.twoFactorPhone?? userProfile.mobilePhone
      this.twoFactorIsMobileVerified = this.twoFactorIsMobileVerified?? userProfile.isMobileVerified;
    });

    this.userFacade.userNeedsToSetTwoFactorAuthentication$.pipe(untilDestroyed(this), take(1))
    .subscribe((result: boolean) => {
      this.hideCloseButtonOnTFAModal = result;
    });

    if (this.twoFactorType === twoFactorType.none || this.twoFactorEnabled === false)
      this.twoFactorType = twoFactorType.sms;
  }

  onClose() {
    this.dialogRef.close();
  }

  onSetTFAType(e: MatRadioChange) {
    this.twoFactorType = Number(e.value);
  }

  onTFASubmit(form: NgForm) {
    if (this.twoFactorType === 1) {
      const accountModel = {
        ...this.userProfile,
        twoFactorType: this.twoFactorType,
        twoFactorEnabled: true
      };
      this.store.dispatch(new UserActions.SaveUserProfileTFATypeEmail(accountModel));
      this.onClose();
    } else if (this.twoFactorType === 2) {
      this.store.dispatch(new UserActions.UpdateUserProfileTFATypeSMS(this.twoFactorType));
    }
  }

  onRequestSMSVerification(form: NgForm) {
    this.twoFactorPhone = form.value.profile_mobile_phone;
    if (this.userProfile.mobilePhone === this.twoFactorPhone && this.twoFactorIsMobileVerified){
      const accountModel = {
        ...this.userProfile,
        userEntityId: this.userEntityId,
        twoFactorType: twoFactorType.sms,
        twoFactorEnabled: true
      };
      this.store.dispatch(new UserActions.VerifySMSSuccess(accountModel));
      this.onClose();
    }else{
      const oRequestSMSVerification = {
        entityId: this.userEntityId,
        body: JSON.stringify(form.value.profile_mobile_phone)
      };
      this.store.dispatch(new UserActions.RequestSMSVerification(oRequestSMSVerification));     
    }
  }

  onVerifySMS(form: NgForm) {
    const verificationCode = form.value.verificationCode;
    const oMobileVerification = {
      userProfile: {
        ...this.userProfile,
        userEntityId: this.userEntityId,
        mobilePhone: this.twoFactorPhone,
        twoFactorEnabled: true
      },
      body: verificationCode
    };
    this.store.dispatch(new UserActions.VerifySMS(oMobileVerification)); 
  }

  onSetTFAStep(n: number) {
    this.TFAStep = n;
    this.store.dispatch(new UserActions.TFAStep(n));
  }

  setMobilePhoneValue() {
    this.accountSecurityTFAForm.setValue({
      'twoFactorType': this.twoFactorType,
      'profile_mobile_phone': this.getPhoneNumber(this.twoFactorPhone)
    });
  }

  getPhoneNumber(nPhoneNumber) {
    if (nPhoneNumber) {
      return nPhoneNumber;
    } else {
      return '';
    }
  }

  resetMobileVerificationForm(){
    this.resetErrorMessage();
    this.resetVerificationCodeInput();
  }

  resetErrorMessage() {
    if (this.SMSVerificationStatus !== SMSVerificationStatus.undefined) {
      this.store.dispatch(new UserActions.ResetSMSVerificationStatus);
    }
  }

  resetVerificationCodeInput(){
    this.mobileVerificationForm.reset();
  }

  resetIsMobileVerified(){
    this.twoFactorIsMobileVerified = false;
  }

  getButtonText(): string {
    if ((this.TFAStep === TFASteps.START_SET_TYPE && this.twoFactorType === 1) || 
        (this.TFAStep === TFASteps.SMS_REQUEST && this.twoFactorIsMobileVerified))
      return "Done";
    else
      return "Next";
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
  }
}